// src/pages/Officer/OfficerStrategies.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  Tooltip,
  Collapse,
  Badge,
  LinearProgress,
  Divider,
  Button,
  Alert,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  Refresh,
  Speed,
  Timeline,
  CheckCircle,
  Cancel,
  AccessTime,
  ExpandMore,
  ExpandLess,
  ShowChart,
  Notifications,
  PlayArrow,
  Pause,
  ErrorOutline,
} from '@mui/icons-material';
import { strategyAPI, alertAPI } from '../../services/api';
import useAlertWebSocket from '../../hooks/useAlertWebSocket';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * StrategyCard Component - Individual strategy display
 */
const StrategyCard = ({ strategy }) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (isActive) => {
    return isActive ? '#00ff88' : '#64748b';
  };

  const totalConditions = strategy.conditions?.length || 0;
  const metConditions = strategy.conditions?.filter(c => c.met)?.length || 0;
  const progress = totalConditions > 0 ? (metConditions / totalConditions) * 100 : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: alpha('#0f1419', 0.6),
        border: '1px solid',
        borderColor: strategy.is_active 
          ? alpha('#00ff88', 0.3) 
          : alpha('#64748b', 0.2),
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: strategy.is_active 
            ? alpha('#00ff88', 0.5) 
            : alpha('#64748b', 0.4),
          boxShadow: `0 4px 12px ${alpha(
            strategy.is_active ? '#00ff88' : '#64748b', 
            0.15
          )}`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: alpha(getStatusColor(strategy.is_active), 0.05),
          borderBottom: '1px solid',
          borderColor: alpha(getStatusColor(strategy.is_active), 0.1),
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {/* Left: Strategy Info */}
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
            {/* Icon */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: alpha(getStatusColor(strategy.is_active), 0.15),
                border: '2px solid',
                borderColor: alpha(getStatusColor(strategy.is_active), 0.3),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShowChart sx={{ 
                color: getStatusColor(strategy.is_active), 
                fontSize: 28 
              }} />
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Strategy Name & Status */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '1.15rem',
                  }}
                >
                  {strategy.strategy_id}
                </Typography>
                <Chip
                  icon={strategy.is_active ? <PlayArrow /> : <Pause />}
                  label={strategy.is_active ? 'ACTIVE' : 'PAUSED'}
                  size="small"
                  sx={{
                    height: 26,
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    bgcolor: alpha(getStatusColor(strategy.is_active), 0.2),
                    color: getStatusColor(strategy.is_active),
                    border: '1px solid',
                    borderColor: alpha(getStatusColor(strategy.is_active), 0.4),
                  }}
                />
              </Stack>

              {/* Symbol & Timeframe */}
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.600',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Symbol:
                  </Typography>
                  <Typography
                    sx={{
                      color: '#00d4ff',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                    }}
                  >
                    {strategy.symbol}
                  </Typography>
                  {strategy.timeframe && (
                    <>
                      <Typography sx={{ color: 'grey.700', fontSize: '0.8rem' }}>•</Typography>
                      <Typography
                        sx={{
                          color: 'grey.400',
                          fontSize: '0.85rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        {strategy.timeframe}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>

              {/* Conditions Progress */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.500',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    Conditions Met
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: progress === 100 ? '#00ff88' : 'grey.400',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                    }}
                  >
                    {metConditions}/{totalConditions}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: alpha('#000', 0.3),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: progress === 100 ? '#00ff88' : '#00d4ff',
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            </Box>
          </Stack>

          {/* Right: Actions */}
          <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            <Tooltip title="View signals">
              <IconButton
                size="small"
                sx={{
                  bgcolor: alpha('#00d4ff', 0.1),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                  '&:hover': {
                    bgcolor: alpha('#00d4ff', 0.2),
                  },
                }}
              >
                <Timeline sx={{ color: '#00d4ff', fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                color: 'grey.500',
                '&:hover': {
                  bgcolor: alpha('#00d4ff', 0.1),
                  color: '#00d4ff',
                },
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Stats Row */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha('#0a0e14', 0.4),
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.05),
        }}
      >
        <Stack direction="row" spacing={3}>
          {/* Total Signals */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'grey.600',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                mb: 0.5,
              }}
            >
              Total Signals
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#00d4ff',
                fontWeight: 700,
                fontFamily: 'monospace',
              }}
            >
              {strategy.total_signals || 0}
            </Typography>
          </Box>

          {/* Success Rate */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'grey.600',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                mb: 0.5,
              }}
            >
              Success Rate
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#00ff88',
                fontWeight: 700,
                fontFamily: 'monospace',
              }}
            >
              {strategy.success_rate || 0}%
            </Typography>
          </Box>

          {/* Last Signal */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'grey.600',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                mb: 0.5,
              }}
            >
              Last Signal
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'grey.400',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
              }}
            >
              {strategy.last_signal || 'Never'}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Expanded Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha('#0a0e14', 0.6),
          }}
        >
          <Stack spacing={2}>
            {/* Conditions List */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#00d4ff',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: '0.7rem',
                  mb: 1.5,
                  display: 'block',
                }}
              >
                Conditions ({totalConditions})
              </Typography>

              {strategy.conditions && strategy.conditions.length > 0 ? (
                <Stack spacing={1}>
                  {strategy.conditions.map((condition, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: alpha('#000', 0.3),
                        border: '1px solid',
                        borderColor: condition.met 
                          ? alpha('#00ff88', 0.3) 
                          : alpha('#666', 0.2),
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                          {condition.met ? (
                            <CheckCircle sx={{ color: '#00ff88', fontSize: 20 }} />
                          ) : (
                            <Cancel sx={{ color: 'grey.700', fontSize: 20 }} />
                          )}
                          <Box>
                            <Typography
                              sx={{
                                color: condition.met ? 'grey.200' : 'grey.500',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                              }}
                            >
                              {condition.name}
                            </Typography>
                            {condition.description && (
                              <Typography
                                sx={{
                                  color: 'grey.600',
                                  fontSize: '0.7rem',
                                  mt: 0.3,
                                }}
                              >
                                {condition.description}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Chip
                          label={condition.met ? 'MET' : 'PENDING'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: condition.met 
                              ? alpha('#00ff88', 0.15) 
                              : alpha('#666', 0.1),
                            color: condition.met ? '#00ff88' : 'grey.600',
                            border: '1px solid',
                            borderColor: condition.met 
                              ? alpha('#00ff88', 0.3) 
                              : alpha('#666', 0.2),
                          }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: 'grey.600',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  No conditions configured
                </Typography>
              )}
            </Box>

            {/* Raw Data (Debug) */}
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#64748b',
                fontSize: '0.7rem',
                fontFamily: 'monospace',
              }}>
                View Raw Data (Debug)
              </summary>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#000', 0.4),
                  border: '1px solid',
                  borderColor: alpha('#666', 0.2),
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.7rem',
                    color: '#64748b',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(strategy, null, 2)}
                </pre>
              </Box>
            </details>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * OfficerStrategies - Main Component
 */
const OfficerStrategies = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [stats, setStats] = useState(null);

  const { showNotification, playNotificationSound } = useNotifications(true);

  /**
   * Fetch strategies from API
   */
  const fetchStrategies = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      console.log('[OfficerStrategies] Fetching strategies...');

      const result = await strategyAPI.listStrategies();

      console.log('[OfficerStrategies] API Response:', result);

      if (result.success) {
        const fetchedStrategies = result.data.strategies || [];
        console.log('[OfficerStrategies] Fetched strategies:', fetchedStrategies.length);
        
        setStrategies(fetchedStrategies);
      } else {
        console.error('[OfficerStrategies] API Error:', result.message);
        setError(result.message || 'Failed to fetch strategies');
      }
    } catch (err) {
      console.error('[OfficerStrategies] Exception:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /**
   * Fetch statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      console.log('[OfficerStrategies] Fetching stats...');
      const result = await alertAPI.getAlertStats('7d');
      console.log('[OfficerStrategies] Stats response:', result);
      
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('[OfficerStrategies] Stats error:', err);
    }
  }, []);

  /**
   * WebSocket handlers for live updates
   */
  const handleConditionUpdate = useCallback((data) => {
    console.log('[OfficerStrategies] Condition Update:', data);
    
    // Update strategy conditions in real-time
    setStrategies(prev => prev.map(strategy => {
      if (strategy.strategy_id === data.strategy_id) {
        return {
          ...strategy,
          conditions: data.conditions || strategy.conditions,
          last_update: new Date().toISOString(),
        };
      }
      return strategy;
    }));
  }, []);

  const handleAlertTriggered = useCallback((data) => {
    console.log('[OfficerStrategies] Alert Triggered:', data);
    
    // Update strategy stats
    setStrategies(prev => prev.map(strategy => {
      if (strategy.strategy_id === data.strategy_id) {
        return {
          ...strategy,
          total_signals: (strategy.total_signals || 0) + 1,
          last_signal: 'Just now',
        };
      }
      return strategy;
    }));

    // Notifications
    playNotificationSound('alert', 'high');
    showNotification({
      title: `Signal: ${data.strategy_id}`,
      body: `New signal detected`,
    });
  }, [playNotificationSound, showNotification]);

  const { isConnected } = useAlertWebSocket(
    handleConditionUpdate,
    handleAlertTriggered
  );

  /**
   * Initial fetch
   */
  useEffect(() => {
    console.log('[OfficerStrategies] Component mounted');
    fetchStrategies(true);
    fetchStats();
  }, [fetchStrategies, fetchStats]);

  /**
   * Handlers
   */
  const handleRefresh = () => {
    console.log('[OfficerStrategies] Manual refresh triggered');
    fetchStrategies(false);
    fetchStats();
  };

  /**
   * Filter strategies by search query
   */
  const filteredStrategies = strategies.filter((strategy) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      strategy.strategy_id?.toLowerCase().includes(query) ||
      strategy.symbol?.toLowerCase().includes(query)
    );
  });

  const activeStrategies = strategies.filter(s => s.is_active).length;
  const totalStrategies = strategies.length;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e14',
          gap: 2,
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: '#00d4ff' }} />
        <Typography variant="body1" sx={{ color: 'grey.400' }}>
          Loading strategies...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#0a0e14',
        minHeight: '100vh',
        color: 'grey.100',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: alpha('#0f1419', 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1920 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2 }}
          >
            {/* Left: Title */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#00d4ff', 0.3)}`,
                }}
              >
                <Speed sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'grey.50',
                  }}
                >
                  Active Strategies
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {activeStrategies} active • {totalStrategies} total
                </Typography>
              </Box>
            </Stack>

            {/* Right: Controls */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Connection Status */}
              <Chip
                label={isConnected ? 'LIVE' : 'OFFLINE'}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  bgcolor: isConnected
                    ? alpha('#00ff88', 0.15)
                    : alpha('#ff4444', 0.15),
                  color: isConnected ? '#00ff88' : '#ff4444',
                  border: '1px solid',
                  borderColor: isConnected
                    ? alpha('#00ff88', 0.3)
                    : alpha('#ff4444', 0.3),
                }}
              />

              {/* Refresh Button */}
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    bgcolor: alpha('#00d4ff', 0.1),
                    border: '1px solid',
                    borderColor: alpha('#00d4ff', 0.2),
                    '&:hover': {
                      bgcolor: alpha('#00d4ff', 0.2),
                    },
                  }}
                >
                  <Refresh
                    sx={{
                      color: '#00d4ff',
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ maxWidth: 1920, py: 3 }}>
        <Stack spacing={3}>
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)}
              sx={{
                bgcolor: alpha('#ff4444', 0.1),
                border: '1px solid',
                borderColor: alpha('#ff4444', 0.3),
                color: '#ff4444',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          {stats && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: alpha('#0f1419', 0.6),
                  border: '1px solid',
                  borderColor: alpha('#00ff88', 0.2),
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: alpha('#00ff88', 0.15),
                      border: '1px solid',
                      borderColor: alpha('#00ff88', 0.3),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TrendingUp sx={{ color: '#00ff88', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
                      Active Strategies
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 700 }}>
                      {activeStrategies}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: alpha('#0f1419', 0.6),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: alpha('#00d4ff', 0.15),
                      border: '1px solid',
                      borderColor: alpha('#00d4ff', 0.3),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Timeline sx={{ color: '#00d4ff', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
                      Total Signals (7d)
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                      {stats.total_alerts || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          )}

          {/* Search */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: alpha('#0f1419', 0.6),
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.1),
              borderRadius: 2,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search strategies by name or symbol..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'grey.600' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#0a0e14', 0.6),
                  color: 'grey.100',
                  '& fieldset': {
                    borderColor: alpha('#00d4ff', 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha('#00d4ff', 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00d4ff',
                  },
                },
              }}
            />
          </Paper>

          {/* Debug Info */}
          {/* <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: alpha('#fb923c', 0.1),
              border: '1px solid',
              borderColor: alpha('#fb923c', 0.3),
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutline sx={{ color: '#fb923c', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fb923c', fontWeight: 600 }}>
                Debug: Showing {filteredStrategies.length} strategies out of {strategies.length} fetched
                {strategies.length === 0 && ' (No data from API - check console for errors)'}
              </Typography>
            </Stack>
          </Paper> */}

          {/* Strategies List */}
          {filteredStrategies.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 8,
                bgcolor: alpha('#0f1419', 0.6),
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.1),
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Speed sx={{ fontSize: 64, color: 'grey.800', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'grey.600', fontWeight: 600, mb: 1 }}>
                {strategies.length === 0 ? 'No strategies configured' : 'No strategies found'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.700', mb: 2 }}>
                {strategies.length === 0 
                  ? 'Contact your administrator to set up trading strategies'
                  : 'Try adjusting your search query'}
              </Typography>
              {strategies.length === 0 && (
                <Typography variant="caption" sx={{ color: 'grey.800', display: 'block', fontFamily: 'monospace' }}>
                  Check browser console for API response details
                </Typography>
              )}
            </Paper>
          ) : (
            <Stack spacing={2}>
              {filteredStrategies.map((strategy, index) => (
                <StrategyCard 
                  key={strategy.strategy_id || strategy.id || index} 
                  strategy={strategy} 
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default OfficerStrategies;
